import jwtDecode from "jwt-decode";
import {useEffect, useState} from "react";
import {API_USERS} from "Services/Endpoints";
import {insertNotification} from "Utils/NotificationsUtils";

export const MAX_PAGE_SIZE_FOR_SERVER_SEARCH = 1000;

export class HttpError extends Error {
    constructor(status, body) {
        super(`HTTP Error: ${status}`);
        this.status = status;
        this.body = body;
    }
}

const HttpCode = {
    OK: 200,
    CREATED: 201,
    ACCEPTED: 202,
    UNAUTHORIZED: 401,
    INVALID: 400,
    NoContent: 204,
    FORBIDDEN: 403,
    BAD_GATEWAY: 502,
};

export const HttpErrorMessage = {
    [HttpCode.INVALID]: "Invalid Request",
    [HttpCode.UNAUTHORIZED]: "Unauthorized action",
    DEFAULT_ERROR_MESSAGE: "HTTP status code not recognised",
};

export function makeGetRequest(url) {
    return fetch(url, getHTTPGetConf())
        .then(handleServerResponse())
        .catch((error) => {
            if (error.message === String(HttpCode.UNAUTHORIZED)) {
                return refreshToken().then(() => makeGetRequest(url));
            }
            throw error;
        });
}

// Explicitly for POST requests that does not require authentication to perform
export async function makeUnauthenticatedPostRequest(url, data) {
    return fetch(url, getHTTPPostConf(data, false))
        .then(async (response) => {
            if (!response || response.status !== HttpCode.OK) {
                throw new HttpError(response.status, await response.json());
            }
            return await response.json();
        });
}
export function makePostRequest(url, data) {
    return fetch(url, getHTTPPostConf(data, true))
        .then(handleServerResponse())
        .catch((error) => {
            if (error.message === String(HttpCode.UNAUTHORIZED)) {
                return refreshToken().then(() => makePostRequest(url, data));
            }
            throw error;
        });
}


export function makeDeleteRequest(url, data) {
    return fetch(url, getHTTPDeleteConf(data))
        .then(handleServerResponse())
        .catch((error) => {
            if (error.message === String(HttpCode.UNAUTHORIZED)) {
                return refreshToken().then(() => makeDeleteRequest(url, data));
            }
            throw error;
        });
}

export function makeUpdateRequest(url, data) {
    return fetch(url, getHTTPUpdateConf(data))
        .then(handleServerResponse())
        .catch((error) => {
            if (error.message === String(HttpCode.UNAUTHORIZED)) {
                return refreshToken().then(() => makeUpdateRequest(url, data));
            }
            throw error;
        });
}

export function makePatchRequest(url, data) {
    return fetch(url, getHTTPPatchConf(data))
        .then(handleServerResponse())
        .catch((error) => {
            if (error.message === String(HttpCode.UNAUTHORIZED)) {
                return refreshToken().then(() => makePatchRequest(url, data));
            }
            throw error;
        });
}

export function makeFormDataUpdateRequest(url, data) {
    return fetch(url, getHTTPFormDataUpdateConf(data))
        .then(handleServerResponse())
        .catch((error) => {
            if (error.message === String(HttpCode.UNAUTHORIZED)) {
                return refreshToken().then(() => makeFormDataUpdateRequest(url, data));
            }
            throw error;
        });
}

export function makeFormDataPostRequest(url, data) {
    return fetch(url, getHTTPFormDataPostConf(data))
        .then(handleServerResponse())
        .catch((error) => {
            if (error.message === String(HttpCode.UNAUTHORIZED)) {
                return refreshToken().then(() => makeFormDataPostRequest(url, data));
            }
            throw error;
        });
}

function handleServerResponse() {
    return async (response) => {
        switch (response.status) {
            case HttpCode.OK:
            case HttpCode.CREATED:
            case HttpCode.ACCEPTED:
                return response.json();
            case HttpCode.UNAUTHORIZED:
                throw new Error(HttpCode.UNAUTHORIZED);
            case HttpCode.INVALID:
                throw response.json();
            case HttpCode.FORBIDDEN:
                throw response.json();
            case HttpCode.NoContent:
                return response;
            case HttpCode.BAD_GATEWAY:
                throw new Error("Bad Gateway");
            default:
                throw new Error(HttpErrorMessage.DEFAULT_ERROR_MESSAGE);
        }
    };
}


const isValidRefreshToken = () => {
    const token = localStorage.getItem("refresh_token");
    if (!token || token === "undefined") {
        return false;
    }
    const currentTime = new Date().getTime();
    const decoded = jwtDecode(token);
    return !((currentTime / 1000) > decoded["exp"]);
};
export const refreshToken = async () => {
    const refreshToken = localStorage.getItem("refresh_token");

    if (!isValidRefreshToken()) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        insertNotification("info", "Session expired, You should login again!", "info");
        window.location.href = "/login";
        return;
    }
    return makePostRequest(API_USERS.refreshToken, {"refresh": refreshToken})
        .then((jsonRes) => {
            const newAccessToken = jsonRes.access;
            localStorage.setItem("access_token", newAccessToken);
            return newAccessToken;
        })
        .catch((error) => {
            throw error;
        });
};
function getHTTPGetConf() {
    const token = localStorage.getItem("access_token");
    return {
        method: "GET",
        headers: new Headers({
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
        }),
    };
}

function getHTTPDeleteConf(data) {
    const token = localStorage.getItem("access_token");

    return {
        method: "DELETE",
        headers: new Headers({
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
        }),
        body: JSON.stringify(data),
    };
}

function getHTTPPostConf(data, authorizationRequired = true) {
    const token = localStorage.getItem("access_token");

    const requestHeader = new Headers({
        "Content-Type": "application/json",
    });

    if (authorizationRequired && token) {
        requestHeader.append("Authorization", `Bearer ${token}`);
    }

    return {
        method: "POST",
        headers: requestHeader,
        body: JSON.stringify(data),
    };
}

function getHTTPFormDataUpdateConf(data) {
    const token = localStorage.getItem("access_token");

    return {
        method: "PUT",
        // Do Not explicitly set the content-type according to:
        // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest_API/Using_FormData_Objects
        headers: new Headers({
            "Authorization": `Bearer ${token}`,
        }),
        body: data,
    };
}

function getHTTPFormDataPostConf(data) {
    const token = localStorage.getItem("access_token");

    return {
        method: "POST",
        // Do Not explicitly set the content-type according to:
        // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest_API/Using_FormData_Objects
        headers: new Headers({
            "Authorization": `Bearer ${token}`,
        }),
        body: data,
    };
}

function getHTTPUpdateConf(data) {
    const token = localStorage.getItem("access_token");

    return {
        method: "PUT",
        headers: new Headers({
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
        }),
        body: JSON.stringify(data),
    };
}

function getHTTPPatchConf(data) {
    const token = localStorage.getItem("access_token");

    return {
        method: "PATCH",
        headers: new Headers({
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
        }),
        body: JSON.stringify(data),
    };
}

export const fetchSelectionOptions = (url, setSelection, nameField = "name", noPage = true) => {
    makeGetRequest(
        (url.includes("?") && noPage) ?
            `${url}&no_page` : noPage ?
                `${url}?no_page` : url,
    ).then((data) => {
        const activeItems = (noPage ? data : data.results).filter((serviceType) => serviceType.is_active !== false);
        setSelection(activeItems.map((serviceType) => {
            const displayText = nameField.split(".").reduce((obj, key) => obj && obj[key], serviceType);
            return {
                key: serviceType.id,
                value: serviceType.id,
                display_text: displayText,
            };
        }));
    }).catch((error) => {
        insertNotification("Errors", "Error getting selections " + JSON.stringify(error), "error");
    });
};

export const fetchSelectionArrayOptions = (url, setSelection, nameField = "name", noPage = true) => {
    return makeGetRequest(
        (url.includes("?") && noPage) ?
            `${url}&no_page` : noPage ?
                `${url}?no_page` : url,
    );
};

export const fetchMultiSelectionOptions = (url, setSelection, query) => {
    let mainQuery = `?page_size=${MAX_PAGE_SIZE_FOR_SERVER_SEARCH}`;
    if (query) {
        mainQuery += `&${query}`;
    }

    makeGetRequest(url + mainQuery).then((data) => {
        setSelection(data.results);
    }).catch((error) => {
        insertNotification("Errors", "Error getting selections " + JSON.stringify(error), "error");
    });
};

export const fetchSelectionPaginatedOptions = (
    url,
    setSelections,
    setInitialSelection,
    matchingID = null,
    textField = "name",
    canUsePageSizes = true,
) => {
    let mainQuery = "";
    if (canUsePageSizes) {
        mainQuery = `?page_size=${MAX_PAGE_SIZE_FOR_SERVER_SEARCH}`;
    }

    makeGetRequest(url + mainQuery).then((data) => {
        setSelections(data.results.map((option) => {
            function getLabel() {
                return `${option[textField] || "-"} [${option.id}] ${option.is_active ? "" : "(Disabled)"}`;
            }

            option.label = getLabel();
            return option;
        }));
        if (setInitialSelection && data.results.length > 0) {
            if (matchingID) {
                setInitialSelection(data.results.find((acc) => Number(acc.id) === Number(matchingID)));
            } else {
                setInitialSelection(data.results[0]);
            }
        }
    }).catch((error) => {
        insertNotification("Errors", "Error getting selections " + error, "error");
    });
};

export const fetchSelectionSimplePaginatedOptions = (
    url,
    setSelections,
    setInitialSelection,
    matchingID = null,
    textField = "name",
) => {
    const mainQuery = `?page_size=${MAX_PAGE_SIZE_FOR_SERVER_SEARCH}`;

    makeGetRequest(url + mainQuery).then((data) => {
        setSelections(data.results.map((option) => {
            function getLabel() {
                return `${option[textField] || "-"}`;
            }

            option.label = getLabel();
            return option;
        }));
        if (setInitialSelection && data.results.length > 0) {
            if (matchingID) {
                setInitialSelection(data.results.find((acc) => Number(acc.id) === Number(matchingID)));
            } else {
                setInitialSelection(data.results[0]);
            }
        }
    }).catch((error) => {
        insertNotification("Errors", "Error getting selections " + error, "error");
    });
};

export const useIsDesktop = () => {
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return isDesktop;
};

export const humanizeText = (fieldName) => {
    let formattedName = fieldName.replace(/_/g, " ");
    formattedName = formattedName.replace(/\b\w/g, (char) => char.toUpperCase());
    return formattedName;
};

export function getSelectionsFromPaginatedResults(data) {
    return data.results.map((result) => {
        return {
            key: result.id,
            value: result.id,
            display_text: result.name,
        };
    });
}
